/**
 * Manual-checkout PROFILE editor API client (Settings) — talks to mttr/app_api
 * `/api/admin/manual-profiles` + `/api/admin/companies/{id}/manual-profile`.
 *
 * PLATFORM-ONLY (server enforces company_type=='platform'; 403 otherwise). A
 * profile defines the FORM FIELDS a customer's manual checkout / check-in
 * screens render; enablement is per-smartbox and lives elsewhere. Token rides
 * in the `access-token` header via apiFetch.
 */

import { ApiError, apiFetch } from "./apiClient";

export const MANUAL_FIELD_TYPES = [
  "text",
  "textarea",
  "barcode",
  "signature",
  "select",
] as const;
export type ManualFieldType = (typeof MANUAL_FIELD_TYPES)[number];

export interface ManualFieldDef {
  key: string;
  label: string;
  type: ManualFieldType;
  required: boolean;
  placeholder?: string | null;
  max_length?: number | null;
  column_span?: number | null;
  select_options?: string[] | null;
}

export interface ManualProfileSummary {
  profile_id: number;
  profile_key: string;
  display_name: string;
  mc_field_count: number;
  mi_field_count: number;
  company_count: number;
}

export interface ManualProfileCompany {
  company_id: number;
  company_name: string;
  company_type: string | null;
  manual_checkout_profile_id: number | null;
}

export interface ManualProfilesResponse {
  profiles: ManualProfileSummary[];
  companies: ManualProfileCompany[];
}

export interface ManualProfileDetail {
  profile_id: number;
  profile_key: string;
  display_name: string;
  mc_fields: ManualFieldDef[];
  mi_fields: ManualFieldDef[];
}

class ManualProfilesApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ManualProfilesApiError";
  }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new ManualProfilesApiError(e.message, e.status) : e;
  }
}

export function fetchManualProfiles(token: string): Promise<ManualProfilesResponse> {
  return request<ManualProfilesResponse>("/api/admin/manual-profiles", token);
}

export function fetchManualProfile(
  token: string,
  profileId: number,
): Promise<ManualProfileDetail> {
  return request<ManualProfileDetail>(`/api/admin/manual-profiles/${profileId}`, token);
}

export function createManualProfile(
  token: string,
  body: {
    profile_key: string;
    display_name: string;
    mc_fields: ManualFieldDef[];
    mi_fields: ManualFieldDef[];
  },
): Promise<{ profile_id: number }> {
  return request<{ profile_id: number }>("/api/admin/manual-profiles", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateManualProfile(
  token: string,
  profileId: number,
  body: {
    display_name: string;
    mc_fields: ManualFieldDef[];
    mi_fields: ManualFieldDef[];
  },
): Promise<{ ok: true; profile_id: number }> {
  return request<{ ok: true; profile_id: number }>(
    `/api/admin/manual-profiles/${profileId}`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function setCompanyManualProfile(
  token: string,
  companyId: number,
  profileId: number | null,
): Promise<{ ok: true; company_id: number; profile_id: number | null }> {
  return request(`/api/admin/companies/${companyId}/manual-profile`, token, {
    method: "POST",
    body: JSON.stringify({ profile_id: profileId }),
  });
}
